import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Plus, Pin, PinOff, Trash2, Archive, ArchiveRestore, Tag,
  CheckSquare, Square, X, Search, Palette, MoreHorizontal, Edit3
} from 'lucide-react';
import { toast } from 'sonner';
import SpeakButton from './SpeakButton';
import DictateButton from './DictateButton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NOTE_COLORS = [
  { name: 'White', value: '#FFFFFF' },
  { name: 'Pink', value: '#FFC8DD' },
  { name: 'Blue', value: '#A2D2FF' },
  { name: 'Green', value: '#B9FBC0' },
  { name: 'Yellow', value: '#FDF0D5' },
  { name: 'Lavender', value: '#CDB4DB' },
  { name: 'Coral', value: '#FFCDB2' },
  { name: 'Mint', value: '#D8F3DC' },
];

export default function KeepNotes() {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showArchived, setShowArchived] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterLabel, setFilterLabel] = useState('');
  const [allLabels, setAllLabels] = useState([]);
  const [editingNote, setEditingNote] = useState(null);
  const [showNewNote, setShowNewNote] = useState(false);
  const [newNote, setNewNote] = useState({ title: '', content: '', color: '#FFFFFF', labels: [], checklist: [], pinned: false });
  const [newCheckItem, setNewCheckItem] = useState('');
  const [editCheckItem, setEditCheckItem] = useState('');
  const [newLabel, setNewLabel] = useState('');

  const fetchNotes = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/notes?archived=${showArchived}`, { withCredentials: true });
      setNotes(data);
    } catch (e) { console.error('Failed to fetch notes:', e); }
    finally { setLoading(false); }
  }, [showArchived]);

  const fetchLabels = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/notes/labels`, { withCredentials: true });
      setAllLabels(data);
    } catch { /* labels endpoint missing — non-critical */ }
  }, []);

  useEffect(() => { fetchNotes(); fetchLabels(); }, [fetchNotes, fetchLabels]);

  const handleCreate = async () => {
    if (!newNote.title.trim() && !newNote.content.trim() && newNote.checklist.length === 0) return;
    try {
      await axios.post(`${API}/notes`, newNote, { withCredentials: true });
      toast.success('Note created!');
      setNewNote({ title: '', content: '', color: '#FFFFFF', labels: [], checklist: [], pinned: false });
      setShowNewNote(false);
      fetchNotes(); fetchLabels();
    } catch (e) { toast.error('Failed to create note'); }
  };

  const handleUpdate = async (noteId, updates) => {
    try {
      await axios.patch(`${API}/notes/${noteId}`, updates, { withCredentials: true });
      fetchNotes(); fetchLabels();
    } catch (e) { toast.error('Failed to update'); }
  };

  const handleDelete = async (noteId) => {
    try {
      await axios.delete(`${API}/notes/${noteId}`, { withCredentials: true });
      toast.success('Note deleted');
      setEditingNote(null);
      fetchNotes();
    } catch (e) { toast.error('Failed to delete'); }
  };

  const addCheckItem = (target, setTarget, item) => {
    if (!item.trim()) return;
    const updated = { ...target, checklist: [...target.checklist, { text: item.trim(), checked: false }] };
    setTarget(updated);
  };

  const toggleCheckItem = (target, setTarget, idx) => {
    const updated = { ...target };
    updated.checklist = [...updated.checklist];
    updated.checklist[idx] = { ...updated.checklist[idx], checked: !updated.checklist[idx].checked };
    setTarget(updated);
  };

  const removeCheckItem = (target, setTarget, idx) => {
    const updated = { ...target };
    updated.checklist = updated.checklist.filter((_, i) => i !== idx);
    setTarget(updated);
  };

  const addLabel = (target, setTarget, label) => {
    if (!label.trim() || target.labels.includes(label.trim())) return;
    setTarget({ ...target, labels: [...target.labels, label.trim()] });
  };

  const removeLabel = (target, setTarget, label) => {
    setTarget({ ...target, labels: target.labels.filter(l => l !== label) });
  };

  // Filter notes
  const filtered = notes.filter(n => {
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      if (!n.title.toLowerCase().includes(q) && !n.content.toLowerCase().includes(q)) return false;
    }
    if (filterLabel && !n.labels.includes(filterLabel)) return false;
    return true;
  });

  const pinnedNotes = filtered.filter(n => n.pinned);
  const otherNotes = filtered.filter(n => !n.pinned);

  if (loading) return <div className="neo-card p-8 text-center animate-pulse-soft">Loading notes...</div>;

  return (
    <div className="space-y-4" data-testid="keep-notes">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <button onClick={() => setShowArchived(false)}
            className={`neo-btn px-4 py-2 text-sm ${!showArchived ? 'bg-[#FDF0D5]' : 'bg-white dark:bg-[#252545]'}`}
            data-testid="notes-active-btn">Notes</button>
          <button onClick={() => setShowArchived(true)}
            className={`neo-btn px-4 py-2 text-sm flex items-center gap-1 ${showArchived ? 'bg-[#CDB4DB]' : 'bg-white dark:bg-[#252545]'}`}
            data-testid="notes-archive-btn"><Archive className="w-4 h-4" /> Archive</button>
        </div>
      </div>

      {/* Search + Filter */}
      <div className="flex gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
            className="neo-input w-full pl-10" placeholder="Search notes..." data-testid="notes-search" />
        </div>
        {allLabels.length > 0 && (
          <select value={filterLabel} onChange={(e) => setFilterLabel(e.target.value)}
            className="neo-input" data-testid="notes-label-filter">
            <option value="">All labels</option>
            {allLabels.map(l => <option key={l} value={l}>{l}</option>)}
          </select>
        )}
      </div>

      {/* New Note */}
      {!showNewNote ? (
        <button onClick={() => setShowNewNote(true)}
          className="neo-btn w-full py-4 bg-white dark:bg-[#252545] border-dashed flex items-center justify-center gap-2"
          data-testid="new-note-btn">
          <Plus className="w-5 h-5" /> Take a note...
        </button>
      ) : (
        <NoteEditor
          note={newNote} setNote={setNewNote}
          checkItem={newCheckItem} setCheckItem={setNewCheckItem}
          labelInput={newLabel} setLabelInput={setNewLabel}
          onSave={handleCreate}
          onCancel={() => { setShowNewNote(false); setNewNote({ title: '', content: '', color: '#FFFFFF', labels: [], checklist: [], pinned: false }); }}
          addCheckItem={addCheckItem} toggleCheckItem={toggleCheckItem} removeCheckItem={removeCheckItem}
          addLabel={addLabel} removeLabel={removeLabel}
          isNew
        />
      )}

      {/* Pinned Section */}
      {pinnedNotes.length > 0 && (
        <>
          <p className="text-xs font-bold uppercase tracking-widest text-gray-400 flex items-center gap-1">
            <Pin className="w-3 h-3" /> Pinned
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {pinnedNotes.map(n => (
              <NoteCard key={n.id} note={n} onEdit={setEditingNote} onUpdate={handleUpdate} />
            ))}
          </div>
        </>
      )}

      {/* Other Notes */}
      {otherNotes.length > 0 && (
        <>
          {pinnedNotes.length > 0 && (
            <p className="text-xs font-bold uppercase tracking-widest text-gray-400">Others</p>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {otherNotes.map(n => (
              <NoteCard key={n.id} note={n} onEdit={setEditingNote} onUpdate={handleUpdate} />
            ))}
          </div>
        </>
      )}

      {filtered.length === 0 && (
        <div className="neo-card p-8 text-center">
          <p className="text-gray-500 font-['Fredoka'] text-lg">
            {showArchived ? 'No archived notes' : 'No notes yet — take your first note!'}
          </p>
        </div>
      )}

      {/* Edit Modal */}
      {editingNote && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" onClick={() => setEditingNote(null)}>
          <div className="w-full max-w-lg" onClick={e => e.stopPropagation()}>
            <NoteEditor
              note={editingNote} setNote={setEditingNote}
              checkItem={editCheckItem} setCheckItem={setEditCheckItem}
              labelInput={newLabel} setLabelInput={setNewLabel}
              onSave={() => {
                const { id, created_at, updated_at, ...rest } = editingNote;
                handleUpdate(id, rest);
                setEditingNote(null);
              }}
              onCancel={() => setEditingNote(null)}
              onDelete={() => handleDelete(editingNote.id)}
              addCheckItem={addCheckItem} toggleCheckItem={toggleCheckItem} removeCheckItem={removeCheckItem}
              addLabel={addLabel} removeLabel={removeLabel}
            />
          </div>
        </div>
      )}
    </div>
  );
}

// ============== NOTE CARD ==============
function NoteCard({ note, onEdit, onUpdate }) {
  const checkedCount = note.checklist.filter(c => c.checked).length;
  const totalCheck = note.checklist.length;

  return (
    <div
      className="neo-card p-4 cursor-pointer hover:shadow-[5px_5px_0px_0px_rgba(0,0,0,1)] transition-all group relative"
      style={{ backgroundColor: note.color === '#FFFFFF' ? undefined : note.color }}
      onClick={() => onEdit({ ...note })}
      data-testid={`note-card-${note.id}`}
    >
      {/* Pin button */}
      <button
        onClick={(e) => { e.stopPropagation(); onUpdate(note.id, { pinned: !note.pinned }); }}
        className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded hover:bg-black/10"
        data-testid={`pin-${note.id}`}
      >
        {note.pinned ? <PinOff className="w-4 h-4" /> : <Pin className="w-4 h-4" />}
      </button>

      {/* Title */}
      {note.title && <h4 className="font-bold font-['Fredoka'] mb-1 pr-6">{note.title}</h4>}

      {/* Content */}
      {note.content && <p className="text-sm text-gray-700 dark:text-gray-300 mb-2 line-clamp-4">{note.content}</p>}

      {/* Checklist preview */}
      {totalCheck > 0 && (
        <div className="mb-2">
          {note.checklist.slice(0, 4).map((item, i) => (
            <div key={i} className="flex items-center gap-2 text-sm">
              {item.checked ? <CheckSquare className="w-3.5 h-3.5 text-green-600" /> : <Square className="w-3.5 h-3.5 text-gray-400" />}
              <span className={item.checked ? 'line-through text-gray-400' : ''}>{item.text}</span>
            </div>
          ))}
          {totalCheck > 4 && <p className="text-xs text-gray-400 mt-1">+{totalCheck - 4} more</p>}
        </div>
      )}

      {/* Labels */}
      {note.labels.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2">
          {note.labels.map(l => (
            <span key={l} className="px-2 py-0.5 bg-black/10 rounded-full text-[10px] font-bold">{l}</span>
          ))}
        </div>
      )}

      {/* Quick actions */}
      <div className="flex items-center gap-1 mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
        <button onClick={(e) => { e.stopPropagation(); onUpdate(note.id, { archived: !note.archived }); }}
          className="p-1 rounded hover:bg-black/10" data-testid={`archive-${note.id}`}>
          {note.archived ? <ArchiveRestore className="w-3.5 h-3.5" /> : <Archive className="w-3.5 h-3.5" />}
        </button>
      </div>
    </div>
  );
}

// ============== NOTE EDITOR ==============
function NoteEditor({ note, setNote, checkItem, setCheckItem, labelInput, setLabelInput, onSave, onCancel, onDelete, addCheckItem, toggleCheckItem, removeCheckItem, addLabel, removeLabel, isNew }) {
  const [showColors, setShowColors] = useState(false);
  const [showLabels, setShowLabels] = useState(false);

  return (
    <div className="neo-card p-5 animate-bounce-in" style={{ backgroundColor: note.color === '#FFFFFF' ? undefined : note.color }}
      data-testid={isNew ? 'new-note-editor' : 'edit-note-editor'}>
      {/* Title */}
      <input type="text" value={note.title} onChange={(e) => setNote({ ...note, title: e.target.value })}
        className="w-full bg-transparent font-bold font-['Fredoka'] text-lg mb-2 focus:outline-none" placeholder="Title"
        data-testid="note-title-input" />

      {/* Content */}
      <div className="relative mb-3">
        <textarea value={note.content} onChange={(e) => setNote({ ...note, content: e.target.value })}
          className="w-full bg-transparent resize-none text-sm font-['Nunito'] focus:outline-none pr-20" placeholder="Take a note..." rows={3}
          data-testid="note-content-input" />
        <div className="absolute top-0 right-0 flex items-center gap-1">
          <DictateButton
            onAppend={(said) => setNote({ ...note, content: (note.content ? note.content + ' ' : '') + said })}
            label="Diktera"
            size="sm"
          />
          {note.content?.trim() && (
            <SpeakButton text={`${note.title ? note.title + '. ' : ''}${note.content}`} label="" size="sm" />
          )}
        </div>
      </div>

      {/* Checklist */}
      {note.checklist.length > 0 && (
        <div className="mb-3 space-y-1">
          {note.checklist.map((item, i) => (
            <div key={i} className="flex items-center gap-2">
              <button onClick={() => toggleCheckItem(note, setNote, i)}>
                {item.checked ? <CheckSquare className="w-4 h-4 text-green-600" /> : <Square className="w-4 h-4 text-gray-400" />}
              </button>
              <span className={`flex-1 text-sm ${item.checked ? 'line-through text-gray-400' : ''}`}>{item.text}</span>
              <button onClick={() => removeCheckItem(note, setNote, i)} className="text-gray-400 hover:text-red-500">
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Add checklist item */}
      <div className="flex gap-2 mb-3">
        <input type="text" value={checkItem} onChange={(e) => setCheckItem(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { addCheckItem(note, setNote, checkItem); setCheckItem(''); } }}
          className="neo-input flex-1 !py-1.5 text-sm" placeholder="+ Add checklist item"
          data-testid="note-check-input" />
        <button onClick={() => { addCheckItem(note, setNote, checkItem); setCheckItem(''); }}
          className="neo-btn px-3 py-1 bg-white text-sm" data-testid="note-add-check">
          <Plus className="w-4 h-4" />
        </button>
      </div>

      {/* Labels */}
      {note.labels.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-3">
          {note.labels.map(l => (
            <span key={l} className="px-2 py-0.5 bg-black/10 rounded-full text-xs font-bold flex items-center gap-1">
              {l}
              <button onClick={() => removeLabel(note, setNote, l)}><X className="w-3 h-3" /></button>
            </span>
          ))}
        </div>
      )}

      {/* Toolbar */}
      <div className="flex items-center gap-1 border-t border-black/10 pt-3 flex-wrap">
        {/* Color picker */}
        <div className="relative">
          <button onClick={() => { setShowColors(!showColors); setShowLabels(false); }}
            className="p-2 rounded-lg hover:bg-black/10" data-testid="note-color-btn">
            <Palette className="w-4 h-4" />
          </button>
          {showColors && (
            <div className="absolute bottom-10 left-0 neo-card p-2 flex gap-1 z-50">
              {NOTE_COLORS.map(c => (
                <button key={c.value} onClick={() => { setNote({ ...note, color: c.value }); setShowColors(false); }}
                  className={`w-7 h-7 rounded-full border-2 ${note.color === c.value ? 'border-black scale-110' : 'border-gray-300'}`}
                  style={{ backgroundColor: c.value }} title={c.name} data-testid={`color-${c.name.toLowerCase()}`} />
              ))}
            </div>
          )}
        </div>

        {/* Labels */}
        <div className="relative">
          <button onClick={() => { setShowLabels(!showLabels); setShowColors(false); }}
            className="p-2 rounded-lg hover:bg-black/10" data-testid="note-label-btn">
            <Tag className="w-4 h-4" />
          </button>
          {showLabels && (
            <div className="absolute bottom-10 left-0 neo-card p-3 z-50 min-w-[180px]">
              <div className="flex gap-1 mb-2">
                <input type="text" value={labelInput} onChange={(e) => setLabelInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { addLabel(note, setNote, labelInput); setLabelInput(''); } }}
                  className="neo-input flex-1 !py-1 text-sm" placeholder="Add label" data-testid="note-label-input" />
                <button onClick={() => { addLabel(note, setNote, labelInput); setLabelInput(''); }}
                  className="neo-btn px-2 py-1 bg-[#B9FBC0] text-xs">Add</button>
              </div>
            </div>
          )}
        </div>

        {/* Pin */}
        <button onClick={() => setNote({ ...note, pinned: !note.pinned })}
          className={`p-2 rounded-lg hover:bg-black/10 ${note.pinned ? 'text-yellow-600' : ''}`} data-testid="note-pin-btn">
          {note.pinned ? <PinOff className="w-4 h-4" /> : <Pin className="w-4 h-4" />}
        </button>

        <div className="flex-1" />

        {/* Delete (edit mode only) */}
        {onDelete && (
          <button onClick={onDelete} className="p-2 rounded-lg hover:bg-red-100 text-red-500" data-testid="note-delete-btn">
            <Trash2 className="w-4 h-4" />
          </button>
        )}

        {/* Cancel & Save */}
        <button onClick={onCancel} className="neo-btn px-3 py-1.5 bg-gray-200 text-sm">Cancel</button>
        <button onClick={onSave} className="neo-btn px-4 py-1.5 bg-[#B9FBC0] text-sm" data-testid="note-save-btn">
          {isNew ? 'Create' : 'Save'}
        </button>
      </div>
    </div>
  );
}
